import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import {PersonalDetails} from '@/Hooks/PlayerProfileApi/types';

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 20,
    paddingHorizontal: 24,
    marginBottom: 18,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: '#FAF6EB',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTextCol: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    color: '#262322',
  },
  subtitle: {
    fontSize: 12,
    color: '#888280',
    marginTop: 2,
  },
  chevronBtn: {
    padding: 6,
  },
  fieldsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 16,
  },
  fieldChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    backgroundColor: '#FAF6EB',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 14,
    flexGrow: 1,
    minWidth: 170,
  },
  fieldIcon: {
    marginTop: 1,
  },
  fieldLabel: {
    fontSize: 10.5,
    fontWeight: '700',
    color: '#888280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  fieldValue: {
    fontSize: 13.5,
    fontWeight: '600',
    color: '#262322',
    marginTop: 2,
  },
});

const FIELD_ICONS: Record<string, string> = {
  email: 'mail',
  pincode: 'map-pin',
  timezone: 'clock',
  phone: 'phone',
  gender: 'user',
  dob: 'calendar',
  alternate_phone: 'phone',
};

const FIELD_LABELS: Record<string, string> = {
  email: 'Email',
  pincode: 'Pin/Zip',
  timezone: 'Timezone',
  phone: 'Phone',
  gender: 'Gender',
  dob: 'Date of birth',
  alternate_phone: 'Alt. phone',
};

// Matches the design order (Email, Pin/Zip, Timezone, Phone, Gender, Date of
// birth, Alt. phone), not object insertion order.
const FIELD_ORDER: (keyof PersonalDetails)[] = [
  'email',
  'pincode',
  'timezone',
  'phone',
  'gender',
  'dob',
  'alternate_phone',
];

function formatDob(iso: string | null): string | null {
  if (!iso) {
    return null;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function displayValue(key: keyof PersonalDetails, raw: string | null): string | null {
  if (!raw) {
    return null;
  }
  if (key === 'dob') {
    return formatDob(raw);
  }
  if (key === 'gender') {
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  }
  return raw;
}

type Props = {
  details: PersonalDetails;
};

// Owner-only card, collapsible -- placed right below the hero header. Never
// rendered for a visitor (see caller in PlayerProfile/index.tsx, which only
// builds and passes `details` when `is_owner` is true; the data itself comes
// from the app-wide dashboard summary query, not the profile response).
export default function PersonalDetailsCard({details}: Props) {
  const [expanded, setExpanded] = useState(true);
  const fields = FIELD_ORDER
    .map(key => ({key, value: displayValue(key, details[key])}))
    .filter((f): f is {key: keyof PersonalDetails; value: string} => f.value != null);

  if (!fields.length) {
    return null;
  }

  return (
    <Box style={styles.card}>
      <Pressable
        testID="personal-details-toggle"
        style={styles.headerRow}
        onPress={() => setExpanded(e => !e)}>
        <View style={styles.headerIcon}>
          <Feather name="user" size={17} color="#4D3F37" />
        </View>
        <View style={styles.headerTextCol}>
          <CCText style={styles.title}>Personal details</CCText>
          <CCText style={styles.subtitle}>
            Email, phone, DOB and more · visible only to you
          </CCText>
        </View>
        <View style={styles.chevronBtn}>
          <Feather
            name={expanded ? 'chevron-up' : 'chevron-down'}
            size={18}
            color="#4D3F37"
          />
        </View>
      </Pressable>

      {expanded ? (
        <View style={styles.fieldsRow}>
          {fields.map(({key, value}) => (
            <View key={key} style={styles.fieldChip}>
              <Feather
                name={FIELD_ICONS[key]}
                size={15}
                color="#888280"
                style={styles.fieldIcon}
              />
              <View style={{flexShrink: 1, minWidth: 0}}>
                <CCText style={styles.fieldLabel}>{FIELD_LABELS[key]}</CCText>
                <CCText style={styles.fieldValue} numberOfLines={1}>
                  {value}
                </CCText>
              </View>
            </View>
          ))}
        </View>
      ) : null}
    </Box>
  );
}
